import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { id: "feature", label: "Feature idea" },
  { id: "sound", label: "Kit / sound" },
  { id: "bug", label: "Something broken" },
  { id: "general", label: "General" },
];

export const SuggestionModal = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("feature");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const trigger = () => {
    if (!user) {
      toast.message("Please log in to share your feedback.");
      return;
    }
    setOpen(true);
  };

  const submit = async () => {
    if (message.trim().length < 3) {
      toast.error("Please type a little more detail.");
      return;
    }
    setSending(true);
    try {
      await api.post("/member-suggestions", { message: message.trim(), category });
      toast.success("Thank you! Your suggestion has been sent. 🙌");
      setMessage("");
      setCategory("feature");
      setOpen(false);
    } catch (e) {
      toast.error("Could not send — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        onClick={trigger}
        className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.03] px-4 py-2 text-xs font-tab uppercase tracking-widest text-slate-300 transition-colors hover:border-[#00f0ff]/40 hover:text-[#00f0ff]"
        data-testid="open-suggestion-button"
      >
        <MessageSquarePlus className="w-3.5 h-3.5" />
        Share feedback
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0b0c10] border-white/10 text-slate-100" data-testid="suggestion-modal">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Share your feedback</DialogTitle>
            <DialogDescription className="text-slate-400">
              Got an idea to make BeatFlow better? We read every suggestion from our members.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap gap-2 py-1">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`rounded-full px-3 py-1.5 text-xs font-tab uppercase tracking-widest transition-colors ${
                  category === c.id
                    ? "bg-[#00f0ff] text-black"
                    : "border border-white/15 text-slate-300 hover:border-[#00f0ff]/40"
                }`}
                data-testid={`suggestion-category-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Tell us what you'd love to see, or what felt off…"
            rows={5}
            maxLength={2000}
            className="bg-white/[0.03] border-white/10 text-slate-100 placeholder:text-slate-600"
            data-testid="suggestion-textarea"
          />

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="suggestion-cancel">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={sending}
              className="bg-[#00f0ff] text-black hover:bg-[#33f4ff]"
              data-testid="suggestion-submit"
            >
              {sending ? "Sending…" : "Send suggestion"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
